import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { masterUserRoleSchema, type MasterUserRole, type SessionPolicy } from '@easyflow/shared';
import {
  DEFAULT_SESSION_POLICY,
  SessionTimeoutBanner,
  decodeJwtPayload,
  getSessionIdleStore,
  sessionDeadlineStorageKey,
  sessionExpiredStorageKey,
  sessionPolicyToConfig,
  type SessionDurations,
  type SessionExpireEvent,
  type SessionStartReason,
} from '@easyflow/ui-frontend';
import apiClient from '@/lib/api';
import ForceLogoutDialog from '@/components/ForceLogoutDialog';
import {
  AUTH_STORAGE_KEYS,
  migrateAuthStorage,
  clearAllAuthStorage,
  readStoredSessionPolicy,
  storeSessionPolicy,
} from '@/constants/auth';
import { getLoginRoutePath } from '@/lib/paths';
import {
  SESSION_EXPIRED_EVENT,
  TOKEN_REFRESHED_EVENT,
  expireSession,
  performLogoutCleanup,
  refreshToken as refreshMasterToken,
  type TokenRefreshedDetail,
} from '@/lib/tokenRefresh';
import {
  syncNotificationPrefsToMasterSW,
  clearNotificationPrefsSnapshot,
} from '@/lib/notificationPrefsSync';
import {
  initMasterPushSubscription,
  unsubscribeFromMasterPush,
} from '@/lib/masterPushSubscription';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string | null;
  tenant_id?: string | null;
  tenantSlug: string;
  tenantName?: string;
  role: MasterUserRole;
  iat?: number;
  exp?: number;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (token: string, refreshTokenValue?: string, sessionPolicy?: SessionPolicy) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_SCOPE = 'master';
const LOGGED_OUT_STATE: AuthState = { user: null, token: null, isAuthenticated: false, isLoading: false };

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Ruolo master dal contratto condiviso (`masterUserRoleSchema`), non una lista locale. */
function parseMasterRole(value: unknown): MasterUserRole | undefined {
  const parsed = masterUserRoleSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

type MasterTokenRejection = 'invalid' | 'not_master' | 'missing_fields';

interface MasterTokenIdentity {
  readonly user: User;
  readonly expMs: number;
}

function isTokenExpired(identity: MasterTokenIdentity): boolean {
  return identity.expMs <= Date.now();
}

/**
 * Identità master letta dal JWT (senza verifica di firma: la fa il backend).
 * La scadenza NON è un rifiuto: la decide il chiamante (`isTokenExpired`),
 * perché un access scaduto con refresh valido è una sessione da riprendere.
 */
function readMasterToken(token: string): MasterTokenIdentity | MasterTokenRejection {
  const payload = decodeJwtPayload(token);
  if (!payload) return 'invalid';
  const exp = asNumber(payload.exp);
  if (exp === undefined) return 'invalid';
  if (payload.is_master_tenant !== true) return 'not_master';
  const email = asString(payload.email);
  const role = parseMasterRole(payload.role);
  const id = asString(payload.id) ?? asString(payload.userId);
  if (!email || !role || !id) return 'missing_fields';
  return {
    expMs: exp * 1000,
    user: {
      id,
      email,
      firstName: asString(payload.firstName) ?? '',
      lastName: asString(payload.lastName) ?? '',
      tenantId: asString(payload.tenant_id) ?? asString(payload.tenantId) ?? null,
      tenantSlug: asString(payload.tenant_slug) ?? asString(payload.tenantSlug) ?? '',
      role,
      iat: asNumber(payload.iat),
      exp,
    },
  };
}

function isTokenRefreshedDetail(value: unknown): value is TokenRefreshedDetail {
  return typeof value === 'object' && value !== null && typeof (value as { token?: unknown }).token === 'string'
    && (value as { token: string }).token.length > 0;
}

function isIdentity(value: MasterTokenIdentity | MasterTokenRejection): value is MasterTokenIdentity {
  return typeof value === 'object';
}

function readTimestamp(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const n = Number(raw.split(':')[0]);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/**
 * La deadline di inattività del core (persistita cross-tab) è già passata?
 * Senza deadline persistita conta il marker di scadenza lasciato da un'altra
 * tab; senza nessuno dei due (primo avvio) la sessione è ancora recuperabile.
 */
function idleDeadlinePassed(): boolean {
  const deadline = readTimestamp(sessionDeadlineStorageKey(SESSION_SCOPE));
  if (deadline !== null) return deadline <= Date.now();
  return readTimestamp(sessionExpiredStorageKey(SESSION_SCOPE)) !== null;
}

function currentSessionDurations(): SessionDurations {
  const policy = readStoredSessionPolicy();
  return policy ? sessionPolicyToConfig(policy) : DEFAULT_SESSION_POLICY;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });
  /** Motivo con cui avviare il core alla prossima autenticazione (login = attività). */
  const sessionStartReasonRef = useRef<SessionStartReason>('restore');

  const navigate = useNavigate();
  // `navigate` cambia identità a ogni cambio di rotta: i callback di sessione
  // passano da un ref così gli effect dipendono solo dallo stato di auth.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const activateSession = (identity: MasterTokenIdentity, token: string) => {
    apiClient.setToken(token);
    setAuthState({ user: identity.user, token, isAuthenticated: true, isLoading: false });
    // Sync notification preferences to IndexedDB so the master SW can
    // gate pushes by category and quiet hours on this origin.
    void syncNotificationPrefsToMasterSW();
    // Ensure a PushManager subscription exists on this origin so the
    // backend can deliver Web Pushes to this device/browser.
    void initMasterPushSubscription();
  };

  const dropSession = () => {
    clearAllAuthStorage();
    apiClient.clearToken();
    setAuthState(LOGGED_OUT_STATE);
  };

  useEffect(() => {
    migrateAuthStorage();
    void checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap una sola volta al mount
  }, []);

  const checkSession = async () => {
    try {
      const storedToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
      if (!storedToken) {
        dropSession();
        return;
      }

      let identity = readMasterToken(storedToken);
      let token = storedToken;

      if (!isIdentity(identity)) {
        console.warn(`[Auth] Stored master token rejected (${identity}), clearing`);
        dropSession();
        return;
      }

      if (isTokenExpired(identity)) {
        // Access scaduto: la sessione è ancora viva solo se esiste un refresh
        // token e la deadline di inattività del core non è passata.
        const hasRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN) !== null;
        if (!hasRefreshToken || idleDeadlinePassed()) {
          await performLogoutCleanup();
          dropSession();
          return;
        }
        const outcome = await refreshMasterToken();
        if (outcome.status === 'refreshed') {
          token = outcome.token;
          const refreshedIdentity = readMasterToken(outcome.token);
          if (!isIdentity(refreshedIdentity)) {
            console.warn(`[Auth] Refreshed master token rejected (${refreshedIdentity}), clearing`);
            await performLogoutCleanup();
            dropSession();
            return;
          }
          identity = refreshedIdentity;
        } else if (outcome.status === 'rejected') {
          // Refresh token respinto: credenziali locali già chiuse dal modulo di
          // refresh (nessuna revoca: cancellerebbe anche le sessioni altrui).
          dropSession();
          return;
        }
        // 'unavailable' (rete/5xx): credenziali intatte, si riprende con
        // l'identità del token scaduto; il core e l'interceptor 401 riproveranno
        // il rinnovo. Nessuna revoca per un disservizio transitorio.
      }

      sessionStartReasonRef.current = 'restore';
      activateSession(identity, token);
    } catch (error) {
      console.error('[Auth] Session check failed:', error);
      dropSession();
    }
  };

  const login = async (token: string, refreshTokenValue?: string, sessionPolicy?: SessionPolicy) => {
    const identity = readMasterToken(token);
    if (!isIdentity(identity)) {
      throw new Error(`[Auth] Login token rejected (${identity})`);
    }
    if (isTokenExpired(identity)) {
      throw new Error('[Auth] Login token rejected (expired)');
    }
    localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
    if (refreshTokenValue) {
      localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshTokenValue);
    }
    if (sessionPolicy) {
      storeSessionPolicy(sessionPolicy);
    }
    sessionStartReasonRef.current = 'login';
    activateSession(identity, token);
  };

  const logout = useCallback(async (skipCleanup = false) => {
    try {
      if (!skipCleanup) {
        await performLogoutCleanup();
      }
      // Unsubscribe from push BEFORE clearing the token so the backend request
      // is authenticated. If this fails we still proceed with local cleanup.
      await unsubscribeFromMasterPush();
    } catch (error) {
      console.error('[Auth] Logout request failed:', error);
    } finally {
      getSessionIdleStore(SESSION_SCOPE).stop();
      clearAllAuthStorage();
      apiClient.clearToken();
      // Wipe the notification prefs snapshot so the master SW does not apply
      // a stale gating matrix for the previous session on shared devices.
      void clearNotificationPrefsSnapshot();
      setAuthState(LOGGED_OUT_STATE);
      navigateRef.current(getLoginRoutePath());
    }
  }, []);

  // Core condiviso di sessione per inattività: avviato quando autenticati,
  // fermato dal logout (esplicito, forzato o per scadenza).
  useEffect(() => {
    if (!authState.isAuthenticated) return;
    const store = getSessionIdleStore(SESSION_SCOPE);
    const reason = sessionStartReasonRef.current;
    sessionStartReasonRef.current = 'restore';
    store.start(
      {
        scope: SESSION_SCOPE,
        ...currentSessionDurations(),
        getAccessToken: () => localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN),
        onRefreshNeeded: () => refreshMasterToken(),
        onExpire: (event: SessionExpireEvent) => {
          // Solo la tab initiator revoca lato server; le altre puliscono e basta.
          expireSession({ revoke: event.isInitiator });
          void logout(true);
        },
      },
      { reason },
    );
  }, [authState.isAuthenticated, logout]);

  useEffect(() => {
    const handleSessionExpired = () => {
      void logout(true);
    };

    const handleForceLogout = async (e: Event) => {
      const detail: unknown = e instanceof CustomEvent ? e.detail : undefined;
      await performLogoutCleanup();
      getSessionIdleStore(SESSION_SCOPE).stop();
      clearAllAuthStorage();
      apiClient.clearToken();
      setAuthState(LOGGED_OUT_STATE);
      window.dispatchEvent(new CustomEvent('session:force-logout-ui', { detail }));
    };

    const handleTokenRefreshed = (e: Event) => {
      if (!(e instanceof CustomEvent)) return;
      const detail: unknown = e.detail;
      if (!isTokenRefreshedDetail(detail)) return;
      const newToken = detail.token;
      setAuthState(prev => (prev.isAuthenticated ? { ...prev, token: newToken } : prev));
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener('session:force-logout', handleForceLogout);
    window.addEventListener(TOKEN_REFRESHED_EVENT, handleTokenRefreshed);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener('session:force-logout', handleForceLogout);
      window.removeEventListener(TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
    };
  }, [logout]);

  const keepSessionAlive = useCallback(async () => {
    await refreshMasterToken();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
      }}
    >
      {children}
      {authState.isAuthenticated && (
        <SessionTimeoutBanner
          scope={SESSION_SCOPE}
          onKeepAlive={keepSessionAlive}
          onLogout={() => logout()}
        />
      )}
      <ForceLogoutDialog onConfirm={() => navigate(getLoginRoutePath())} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
